/* SKERVA product page: offer (Solo/Duo), per-pair lens + frame selection, gallery, Shopify Ajax
   cart, drawer and sticky bar. No dependencies; without JS the form still posts to /cart/add. */
(() => {
  const root = document.querySelector('[data-sk]');
  if (!root) return;

  const $ = (s, c = root) => c.querySelector(s);
  const $$ = (s, c = root) => Array.from(c.querySelectorAll(s));
  const data = JSON.parse($('[data-sk-json]').textContent);
  const routes = { add: root.dataset.cartAdd, change: root.dataset.cartChange, cart: root.dataset.cart };
  const variants = data.variants;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const handle = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  /* ---------- Money ---------- */
  const fmt = (cents) => {
    const n = cents / 100;
    const sep = (d, t, s) => { const [i, f] = n.toFixed(d).split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (d ? s + f : ''); };
    return data.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: sep(2, ',', '.'), amount_no_decimals: sep(0, ',', '.'),
      amount_with_comma_separator: sep(2, '.', ','), amount_no_decimals_with_comma_separator: sep(0, '.', ','),
    }[k] ?? n.toFixed(2)));
  };
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- State ---------- */
  const find = (frame, lens) => variants.find((v) => v.frame === frame && v.lens === lens);
  const lensRadios = $$('[data-sk-lens]');
  const frameRadios = $$('[data-sk-frame]');
  const lensOrder = lensRadios.map((r) => r.value);
  const initial = { frame: frameRadios.find((r) => r.checked)?.value, lens: lensRadios.find((r) => r.checked)?.value };
  const state = { offer: 'solo', active: 0, pairs: [{ ...initial }, { ...initial }] };
  const pair = () => state.pairs[state.active];
  const chosen = () => (state.offer === 'duo' ? state.pairs : [state.pairs[0]]).map((p) => find(p.frame, p.lens));

  /* ---------- Elements ---------- */
  const atc = $('[data-sk-atc]');
  const atcLabel = $('[data-sk-atc-label]');
  const msg = $('[data-sk-msg]');
  const variantInput = $('[data-sk-variant-input]');
  const pairsBox = $('[data-sk-pairs]');
  const tabs = $$('[data-sk-pair-tab]');
  const stickyBtn = $('[data-sk-sticky-btn]');

  /* ---------- Gallery ---------- */
  const track = $('[data-sk-track]');
  const allSlides = track ? $$('.sk-stage__slide', track) : [];
  const slides = () => allSlides.filter((s) => !s.hidden);
  const idxEl = $('[data-sk-index]');
  const totalEl = $('[data-sk-total]');
  const currentIndex = () => (track && track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);
  let userScrolling = false;
  ['pointerdown', 'touchstart', 'wheel'].forEach((e) => track?.addEventListener(e, () => { userScrolling = true; }, { passive: true }));
  const goTo = (i, smooth = true) => {
    const list = slides();
    if (!track || !list.length) return;
    userScrolling = false;
    const n = (i + list.length) % list.length;
    track.scrollTo({ left: n * track.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  };
  const showFrame = (frame, smooth = true) => {
    const i = slides().findIndex((s) => s.dataset.frame === frame);
    if (i >= 0) goTo(i, smooth);
  };
  let raf, settle;
  track?.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => { if (idxEl) idxEl.textContent = currentIndex() + 1; });
    clearTimeout(settle);
    if (!userScrolling) return;
    // A customer swipe that settles on a photo selects that frame for the pair being edited.
    settle = setTimeout(() => {
      userScrolling = false;
      const s = slides()[currentIndex()];
      if (s && s.dataset.frame !== pair().frame) { pair().frame = s.dataset.frame; render({ scroll: false, user: true }); }
    }, 160);
  }, { passive: true });
  const step = (d) => {
    const list = slides();
    const n = (currentIndex() + d + list.length) % list.length;
    pair().frame = list[n].dataset.frame;
    render({ user: true });
  };
  $('[data-sk-prev]')?.addEventListener('click', () => step(-1));
  $('[data-sk-next]')?.addEventListener('click', () => step(1));
  track?.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
  });

  /* ---------- Render ---------- */
  let shownLens = initial.lens;
  const render = ({ scroll = true, user = false } = {}) => {
    const p = pair();
    lensRadios.forEach((r) => { r.checked = r.value === p.lens; });
    $('[data-sk-lens-value]').textContent = p.lens;
    $('[data-sk-frame-value]').textContent = p.frame;

    frameRadios.forEach((r) => {
      const label = r.closest('label');
      const v = find(r.value, p.lens);
      const ok = !!(v && v.available);
      r.checked = r.value === p.frame;
      r.disabled = !ok;
      label.toggleAttribute('data-unavailable', !ok);
      label.querySelector('[data-sk-frame-note]').textContent = ok ? '' : v ? 'Sold out' : `${lensOrder.find((l) => find(r.value, l)) ?? ''} only`;
      const src = label.dataset[`img${p.lens.charAt(0).toUpperCase()}${p.lens.slice(1).toLowerCase()}`] || label.querySelector('img')?.getAttribute('src');
      const img = label.querySelector('img');
      if (img && src && img.getAttribute('src') !== src) { img.removeAttribute('srcset'); img.src = src; }
    });

    // Gallery shows the photos for the lens being edited.
    if (p.lens !== shownLens) {
      allSlides.forEach((s) => { s.hidden = s.dataset.lens !== p.lens; });
      if (totalEl) totalEl.textContent = slides().length;
      shownLens = p.lens;
      showFrame(p.frame, false);
    } else if (scroll) {
      showFrame(p.frame);
    }

    // Offer + pairs
    const duo = state.offer === 'duo';
    if (pairsBox) pairsBox.hidden = !duo;
    tabs.forEach((t, i) => {
      t.setAttribute('aria-selected', String(i === state.active));
      const pp = state.pairs[i];
      $(`[data-sk-pair-summary="${i}"]`).textContent = `${pp.frame}, ${pp.lens}`;
    });

    const picks = chosen();
    const valid = picks.every((v) => v && v.available);
    const v0 = find(state.pairs[0].frame, state.pairs[0].lens);
    const v1 = find(state.pairs[1].frame, state.pairs[1].lens);
    const soloPrice = v0 ? v0.price : 0;
    const duoPrice = v0 && v1 ? v0.price + v1.price - data.duoSavings : 0;
    const total = duo ? duoPrice : soloPrice;
    const soloEl = $('[data-sk-solo-price]');
    const duoEl = $('[data-sk-duo-price]');
    if (soloEl && v0) soloEl.textContent = fmt(soloPrice);
    if (duoEl && duoPrice) duoEl.textContent = fmt(duoPrice);

    atc.disabled = !valid;
    atcLabel.textContent = !valid ? (picks.some((v) => v && !v.available) ? 'Sold out' : 'Unavailable')
      : duo ? `Add the Duo · ${fmt(total)}` : `Add to bag · ${fmt(total)}`;
    if (v0) variantInput.value = v0.id;

    $('[data-sk-sticky-name]').textContent = duo ? 'The Duo · 2 pairs' : `The Solo · ${state.pairs[0].frame}, ${state.pairs[0].lens}`;
    $('[data-sk-sticky-price]').textContent = valid ? fmt(total) : '';
    stickyBtn.textContent = valid ? 'Add to bag' : 'Choose options';

    if (user && state.active === 0 && v0) {
      const url = new URL(location.href);
      url.searchParams.set('variant', v0.id);
      history.replaceState(history.state, '', url);
    }
  };

  /* ---------- Inputs ---------- */
  lensRadios.forEach((r) => r.addEventListener('change', () => {
    const p = pair();
    p.lens = r.value;
    const v = find(p.frame, p.lens);
    msg.textContent = '';
    if (!v || !v.available) {
      const alt = frameRadios.map((f) => f.value).find((f) => { const x = find(f, p.lens); return x && x.available; });
      if (alt) { msg.textContent = `${p.frame} comes with ${lensOrder.filter((l) => find(p.frame, l)).join(' or ').toLowerCase()} lenses only, so we selected ${alt}.`; p.frame = alt; }
    }
    render({ user: true });
  }));
  frameRadios.forEach((r) => r.addEventListener('change', () => { pair().frame = r.value; msg.textContent = ''; render({ user: true }); }));
  $$('[data-sk-offer]').forEach((r) => r.addEventListener('change', () => {
    state.offer = r.value;
    if (state.offer === 'solo') state.active = 0;
    render({ scroll: false });
  }));
  tabs.forEach((t, i) => t.addEventListener('click', () => { state.active = i; msg.textContent = ''; render(); }));

  const selectOffer = (value) => {
    const radio = $(`[data-sk-offer][value="${value}"]`);
    if (radio && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
  };
  const toBuy = (focusSel) => {
    const buy = document.getElementById('buy');
    buy.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    setTimeout(() => ($(focusSel || '[data-sk-offer]:checked') || atc).focus({ preventScroll: true }), 500);
  };
  $$('[data-sk-pick-frame]').forEach((b) => b.addEventListener('click', () => {
    const f = b.dataset.skPickFrame;
    const p = pair();
    p.frame = f;
    if (!find(f, p.lens)) p.lens = lensOrder.find((l) => find(f, l)) || p.lens;
    msg.textContent = '';
    render({ user: true });
    toBuy('[data-sk-frame]:checked');
  }));
  $$('[data-sk-choose]').forEach((b) => b.addEventListener('click', () => { selectOffer(b.dataset.skChoose); toBuy(); }));

  /* ---------- Cart API ---------- */
  const api = async (url, body) => {
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
    return json;
  };

  /* ---------- Drawer ---------- */
  const drawer = $('[data-sk-drawer]');
  const body = $('[data-sk-cart-body]');
  const foot = $('[data-sk-cart-foot]');
  const counts = $$('[data-sk-cart-count]');
  let lastFocus = null;
  const icon = (d) => `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="${d}" stroke="currentColor" stroke-width="1.3"/></svg>`;

  const renderCart = (cart) => {
    counts.forEach((el) => { el.textContent = cart.item_count; el.dataset.count = cart.item_count; });
    if (!cart.item_count) {
      body.innerHTML = '<p class="sk-drawer__empty">Your bag is empty.</p>';
      foot.hidden = true;
      return;
    }
    body.innerHTML = cart.items.map((item) => {
      const img = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=200` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="sk-line__variant">${esc(item.variant_title)}</p>` : '';
      const was = item.original_line_price > item.final_line_price ? `<s>${fmt(item.original_line_price)}</s>` : '';
      return `<div class="sk-line" data-key="${esc(item.key)}">
        ${img ? `<img class="sk-line__img" src="${esc(img)}" alt="" width="76" height="76" loading="lazy">` : '<span class="sk-line__img"></span>'}
        <div>
          <p class="sk-line__title">${esc(item.product_title)}</p>${variant}
          <div class="sk-line__actions">
            <div class="sk-qty">
              <button type="button" data-qty="${item.quantity - 1}" aria-label="Decrease quantity of ${esc(item.variant_title || item.product_title)}">${icon('M3 8h10')}</button>
              <span aria-label="Quantity">${item.quantity}</span>
              <button type="button" data-qty="${item.quantity + 1}" aria-label="Increase quantity of ${esc(item.variant_title || item.product_title)}">${icon('M8 3v10M3 8h10')}</button>
            </div>
            <button type="button" class="sk-line__remove" data-qty="0">Remove</button>
          </div>
        </div>
        <p class="sk-line__price">${was}${fmt(item.final_line_price)}</p>
      </div>`;
    }).join('');

    // Savings come from Shopify's own discount allocations, never from this page's arithmetic.
    const saved = cart.total_discount || 0;
    const title = cart.items.flatMap((i) => i.line_level_discount_allocations || []).map((a) => a.discount_application?.title)[0]
      || (cart.cart_level_discount_applications || [])[0]?.title || 'Saving';
    const saveRow = $('[data-sk-cart-save]');
    saveRow.hidden = !saved;
    $('[data-sk-cart-save-label]').textContent = title;
    $('[data-sk-cart-save-amount]').textContent = `−${fmt(saved)}`;
    $('[data-sk-cart-total]').textContent = fmt(cart.total_price);

    // Honest upsell: one pair in the bag -> show what a second pair actually costs with the Duo saving.
    const upsell = $('[data-sk-upsell]');
    const only = cart.item_count === 1 ? variants.find((v) => v.id === cart.items[0].variant_id) : null;
    upsell.innerHTML = data.duo && only && only.available
      ? `<div class="sk-upsell"><span><strong>Make it a Duo.</strong> Add a second pair for ${fmt(only.price - data.duoSavings)}.</span><button type="button" data-sk-upsell-add="${only.id}">Add pair</button></div>`
      : '';
    foot.hidden = false;
  };
  const refreshCart = async () => renderCart(await api(routes.cart));
  const openDrawer = async () => {
    lastFocus = document.activeElement;
    if (!drawer.open) drawer.showModal();
    document.documentElement.style.overflow = 'hidden';
    setSticky();
    try { await refreshCart(); } catch (e) { body.innerHTML = `<p class="sk-drawer__empty">${esc(e.message)}</p>`; }
  };
  const closeDrawer = () => drawer.open && drawer.close();
  drawer.addEventListener('close', () => { document.documentElement.style.overflow = ''; lastFocus?.focus?.(); checkSticky(); });
  drawer.addEventListener('click', (e) => { if (e.target === drawer) closeDrawer(); });
  $('[data-sk-cart-close]').addEventListener('click', closeDrawer);
  $$('[data-sk-cart-open]').forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); }));

  drawer.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('[data-qty]');
    const upBtn = e.target.closest('[data-sk-upsell-add]');
    if (!qtyBtn && !upBtn) return;
    drawer.setAttribute('aria-busy', 'true');
    try {
      if (upBtn) { await api(routes.add, { items: [{ id: Number(upBtn.dataset.skUpsellAdd), quantity: 1 }] }); await refreshCart(); }
      else renderCart(await api(routes.change, { id: qtyBtn.closest('[data-key]').dataset.key, quantity: Number(qtyBtn.dataset.qty) }));
    } catch (err) {
      body.insertAdjacentHTML('afterbegin', `<p class="sk-form__msg" role="alert">${esc(err.message)}</p>`);
    } finally { drawer.removeAttribute('aria-busy'); }
  });

  /* ---------- Add to bag ---------- */
  const lineItems = () => {
    const picks = chosen();
    if (!picks.every((v) => v && v.available)) return [];
    if (picks.length === 2 && picks[0].id === picks[1].id) return [{ id: picks[0].id, quantity: 2 }];
    return picks.map((v) => ({ id: v.id, quantity: 1 }));
  };
  const addToBag = async () => {
    const items = lineItems();
    if (!items.length) return toBuy();
    [atc, stickyBtn].forEach((b) => b.setAttribute('aria-busy', 'true'));
    msg.textContent = '';
    try { await api(routes.add, { items }); await openDrawer(); }
    catch (err) { msg.textContent = err.message; toBuy(); }
    finally { [atc, stickyBtn].forEach((b) => b.removeAttribute('aria-busy')); }
  };
  $('[data-sk-form]').addEventListener('submit', (e) => { e.preventDefault(); addToBag(); });
  stickyBtn.addEventListener('click', addToBag);

  /* ---------- Sticky bar: visible whenever the main button is off screen ---------- */
  const sticky = $('[data-sk-sticky]');
  let ctaHidden = false;
  const setSticky = () => {
    const show = ctaHidden && !drawer.open;
    sticky.classList.toggle('is-visible', show);
    sticky.toggleAttribute('inert', !show);
    sticky.setAttribute('aria-hidden', String(!show));
    document.body.classList.toggle('sk-has-sticky', show);
  };
  let ticking = false;
  const checkSticky = () => {
    ticking = false;
    const r = atc.getBoundingClientRect();
    ctaHidden = r.bottom < 0 || r.top > innerHeight;
    setSticky();
  };
  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(checkSticky); } }, { passive: true });
  addEventListener('resize', checkSticky, { passive: true });

  render({ scroll: false });
  showFrame(initial.frame, false);
  checkSticky();
})();
